import React, {AriaAttributes, CSSProperties, FocusEvent, ReactNode, useRef} from 'react'

import Popover, {PopoverRef} from '../overlays/Popover.js'
import t from '../../../helpers/Languages.js'
import P from '../typography/P.js'
import {buttonResetStyle} from '../buttons/styles.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'

ensureFocusStyles()

/** aria-label: name of the button for screen readers, the text when it is a string */
interface InfoProps extends Pick<AriaAttributes, 'aria-label'> {
    text: ReactNode
    /** Instead of the info icon */
    icon?: string
    /** Colour of the icon */
    color?: string
    /** Style of the icon button, it has no margins */
    style?: CSSProperties
    /** Style of the text in the popover */
    textStyle?: CSSProperties
    /** Where the popover opens, see Popover */
    placement?: 'left' | 'top-left'
    /** @deprecated Old name of color */
    iconColor?: string
    /** @deprecated Old name of style */
    iconStyle?: CSSProperties
}

/** An info icon button that shows text in a popover while it is hovered or has the keyboard focus. */
export default function Info({text, icon, color, style, textStyle, placement, 'aria-label': ariaLabel, iconColor, iconStyle}: InfoProps) {
    const popover = useRef<PopoverRef>(null)

    const show = () => popover.current?.show()
    const close = () => popover.current?.close()

    const onFocus = (event: FocusEvent<HTMLButtonElement>) => {
        let focusVisible = true
        try {
            focusVisible = event.currentTarget.matches(':focus-visible')
        } catch {
            // Browsers without :focus-visible show it on every focus
        }

        if (focusVisible) {
            show()
        }
    }

    return (
        <Popover
            ref={popover}
            // A hover tooltip: Escape is for the field or modal around it
            ignoreEscape
            content={
                <P style={{padding: 10, width: 300, whiteSpace: 'normal', ...textStyle}}>
                    {text}
                </P>
            }
            placement={placement}
        >
            <button
                type='button'
                className={focusableClass}
                aria-label={ariaLabel || (typeof text === 'string' ? text : t('moreInformation'))}
                // Without a colour it inherits the colour of the text around it (color undefined would be the browser's black)
                style={{...buttonResetStyle, color: color ?? iconColor ?? buttonResetStyle.color, ...iconStyle, ...style}}
                onMouseEnter={show}
                onMouseLeave={close}
                onFocus={onFocus}
                onBlur={close}
            >
                <i aria-hidden className={icon || 'mdi mdi-information'} />
            </button>
        </Popover>
    )
}
